import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Key, Zap, CheckCircle2 } from 'lucide-react';

const ActiveOTPVerifications = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [surgeriesWithOTP, setSurgeriesWithOTP] = useState([]);
  const [otpInputs, setOtpInputs] = useState({});
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const endpoint = user.role === 'doctor' ? '/dashboard/doctor' : '/dashboard/hospital';
      const response = await api.get(endpoint);
      
      const upcomingSurgeries = response.data?.data?.upcomingSurgeries || [];
      const withOTP = upcomingSurgeries.filter(s => s.activeOTP);
      setSurgeriesWithOTP(withOTP);
    } catch (error) {
      toast.error('Failed to load active verifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user.role === 'doctor' || user.role === 'hospital') {
      fetchDashboardData();
    }
  }, [user]);

  const handleVerifyOtp = async (surgeryId, otpId, purpose) => {
    const code = otpInputs[surgeryId];
    if (!code || code.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setVerifyingOtp(true);
    try {
      const endpoint = purpose === 'surgery_start' || purpose === 'surgery_verification' ? `/surgeries/${surgeryId}/start` : `/surgeries/${surgeryId}/complete`;
      const response = await api.post(endpoint, { otpCode: code, otpId });
      if (response.data.success) {
        toast.success(response.data.message || 'OTP Verified Successfully');
        fetchDashboardData();
        setOtpInputs(prev => ({ ...prev, [surgeryId]: '' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-10 space-y-6 bg-[#F8FAFC] min-h-screen">
        <div className="h-10 w-64 bg-slate-200 animate-pulse rounded-xl mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="h-64 bg-white rounded-3xl animate-pulse border border-slate-100" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 sm:space-y-8 bg-[#F8FAFC] min-h-screen pb-20">
      <div className="flex items-center gap-3 mb-8">
        <div className="bg-amber-100 p-3 rounded-2xl">
          <Key className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-[#143E78] tracking-tight">OTP Verifications</h1>
          <p className="text-gray-500 font-medium text-xs sm:text-sm">Manage and verify active OTPs for your assigned surgeries.</p>
        </div>
      </div>

      {surgeriesWithOTP.length > 0 ? (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-[32px] sm:rounded-[40px] p-6 sm:p-8 lg:p-10 shadow-md animate-fadeIn">
          <h3 className="text-xl font-black text-amber-800 flex items-center gap-3 mb-6 sm:mb-8">
            <Zap className="w-6 h-6 text-amber-500" /> Action Required: Active Verifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {surgeriesWithOTP.map(surgery => (
              <div key={surgery._id} className="bg-white p-6 rounded-3xl shadow-sm border border-amber-100 flex flex-col gap-5 transition-transform hover:scale-[1.02]">
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-black text-[#143E78] text-lg truncate">{surgery.surgeryName}</h4>
                    <p className="text-xs font-bold text-gray-500 uppercase truncate mt-1">{surgery.lead?.patientName}</p>
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 text-[10px] font-black uppercase rounded-full whitespace-nowrap ml-3">
                    {surgery.activeOTP.purpose?.replace(/_/g, ' ')}
                  </span>
                </div>
                
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex flex-col items-center justify-center">
                  <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Generated OTP Code</p>
                  <p className="text-3xl font-black tracking-[0.2em] text-gray-800">{surgery.activeOTP.code}</p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 mt-auto">
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="Enter OTP"
                    value={otpInputs[surgery._id] || ''}
                    onChange={(e) => setOtpInputs(prev => ({ ...prev, [surgery._id]: e.target.value.replace(/\D/g, '') }))}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-center tracking-widest"
                  />
                  <button
                    onClick={() => handleVerifyOtp(surgery._id, surgery.activeOTP._id, surgery.activeOTP.purpose)}
                    disabled={verifyingOtp || (otpInputs[surgery._id]?.length !== 6)}
                    className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl text-sm font-black uppercase transition-colors shrink-0"
                  >
                    Verify
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-[32px] p-12 flex flex-col items-center justify-center text-center shadow-sm border border-gray-100">
          <div className="bg-emerald-50 w-20 h-20 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <h3 className="text-2xl font-black text-[#143E78] mb-2">You're All Caught Up!</h3>
          <p className="text-gray-400 font-medium">There are no active OTP verifications requiring your attention at this moment.</p>
        </div>
      )}
    </div>
  );
};

export default ActiveOTPVerifications;
